// DataProblemChatDemo.tsx（替换你当前的文件）
'use client';

import AutoplayChatbox from './autoplay-chatbox';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useGSAP } from '@gsap/react';
import initAnimation from '../animations/data-problem-chat-animation';
import { useLocale } from '@/components/providers/context/locale-context';
import { JContainer } from '@/app/components/ui/layout';

function DataProblemChatDemo() {
    const { t } = useTranslation('data-problem');
    const locale = useLocale();
    const rootRef = useRef<HTMLDivElement>(null);
    const firstLineRef = useRef<HTMLDivElement>(null);
    const secondLineRef = useRef<HTMLDivElement>(null);
    const demoLinesContainerRef = useRef<HTMLDivElement>(null);
    const demoLineLeftRef = useRef<HTMLDivElement>(null);
    const demoLineLeftTargetRef = useRef<HTMLDivElement>(null);
    const demoLineRightRef = useRef<HTMLDivElement>(null);
    const demoLineRightTargetRef = useRef<HTMLDivElement>(null);
    const autoPlayRef = useRef<HTMLDivElement>(null);

    const linesContainerRef = useRef<HTMLDivElement>(null);

    useGSAP(
        () => {
            return initAnimation(
                rootRef.current!,
                linesContainerRef.current!,
                firstLineRef.current!,
                secondLineRef.current!,
                demoLinesContainerRef.current!,
                demoLineLeftRef.current!,
                demoLineRightRef.current!,
                demoLineLeftTargetRef.current!,
                demoLineRightTargetRef.current!,
                autoPlayRef.current!,
            );
        },
        { scope: rootRef },
    );

    return (
        <section
            id="data-problem-imagine"
            ref={rootRef}
            style={{ height: 'var(--simulaix-vh, 100svh)' }}
            className="flex w-full justify-center bg-[var(--dynamic-bg-color)]"
        >
            <JContainer size="lg" padding="none" className="relative">
                <div
                    ref={demoLinesContainerRef}
                    className={clsx(
                        'relative z-10 flex h-full w-full flex-col items-center justify-center',
                    )}
                >
                    <h2 className="hidden">
                        {t('demo.firstLine')}
                        {t('demo.secondLine.part1')}
                        {locale === 'en' ? '\u00A0' : '，'}
                        {t('demo.secondLine.part2')}
                        {t('demo.thirdLine.left')}
                        {t('demo.thirdLine.right')}
                    </h2>
                    <div className="absolute flex w-full -translate-y-[100%] transform flex-col items-center justify-center text-center text-[#FFFFFF99]">
                        <div
                            ref={firstLineRef}
                            className={clsx(
                                'j-t1 flex w-full items-center justify-center text-[#FFFFFF99]',
                            )}
                        >
                            {t('demo.firstLine')}
                        </div>

                        <div
                            ref={secondLineRef}
                            className={clsx(
                                'j-h1 flex w-full items-center justify-center text-[#FFFFFF]',
                            )}
                        >
                            <div className="flex flex-col items-center sm:flex-row">
                                <div>{t('demo.secondLine.part1')}</div>
                                <div className={clsx('hidden sm:block')}>
                                    {locale === 'en' ? '\u00A0' : '，'}
                                </div>
                                <div>{t('demo.secondLine.part2')}</div>
                            </div>
                        </div>
                    </div>

                    <div className="flex w-full flex-col items-center justify-center sm:flex-row">
                        <div
                            ref={demoLineLeftRef}
                            style={{
                                background:
                                    'linear-gradient(90deg, #6B48FF 0%, #E8AAFD 164.41%)',
                                WebkitTextFillColor: 'transparent',
                                WebkitBackgroundClip: 'text',
                                backgroundClip: 'text',
                            }}
                            className={clsx(
                                'j-h1 z-10 inline-block whitespace-nowrap pr-[2px]',
                            )}
                        >
                            {t('demo.thirdLine.left')}
                        </div>
                        <div
                            ref={demoLineRightRef}
                            className={clsx(
                                'j-h1 z-10 inline-block whitespace-nowrap pl-0 pr-[2px] sm:pl-[20px]',
                            )}
                            style={{
                                background:
                                    'linear-gradient(90deg, #6B48FF -185.44%, #E8AAFD 100%)',
                                WebkitTextFillColor: 'transparent',
                                WebkitBackgroundClip: 'text',
                                backgroundClip: 'text',
                            }}
                        >
                            {t('demo.thirdLine.right')}
                        </div>
                    </div>
                </div>

                <div className="absolute left-0 top-0 flex h-[var(--simulaix-vh,100svh)] w-full flex-col items-center justify-center pt-[40px] sm:pt-[60px]">
                    <div className="flex w-full flex-1 items-end justify-start pl-[20px] sm:items-center sm:pl-[80px]">
                        <div
                            ref={demoLineLeftTargetRef}
                            style={{
                                background:
                                    'linear-gradient(90deg, #6B48FF 0%, #E8AAFD 164.41%)',
                                WebkitTextFillColor: 'transparent',
                                WebkitBackgroundClip: 'text',
                                backgroundClip: 'text',
                            }}
                            className={clsx(
                                'j-h1 inline-block whitespace-nowrap pr-[2px] opacity-0',
                            )}
                        >
                            {t('demo.thirdLine.left')}
                        </div>
                    </div>
                    <div className="flex h-[calc(var(--simulaix-vh,100svh)*0.75)] w-full max-w-[880px] shrink-0 px-[20px] sm:h-[480px] sm:w-[55vw] sm:px-0">
                        <div ref={autoPlayRef} className="h-full w-full">
                            <AutoplayChatbox />
                        </div>
                    </div>
                    <div className="flex w-full flex-1 items-start justify-end pr-[20px] sm:items-center sm:pr-[80px]">
                        <div
                            ref={demoLineRightTargetRef}
                            style={{
                                background:
                                    'linear-gradient(90deg, #6B48FF 0%, #E8AAFD 164.41%)',
                                WebkitTextFillColor: 'transparent',
                                WebkitBackgroundClip: 'text',
                                backgroundClip: 'text',
                            }}
                            className={clsx(
                                'j-h1 inline-block whitespace-nowrap pl-0 pr-[2px] opacity-0 sm:pl-[20px]',
                            )}
                        >
                            {t('demo.thirdLine.right')}
                        </div>
                    </div>
                </div>
            </JContainer>
        </section>
    );
}

export default DataProblemChatDemo;
